/**
 * Secure Storage Module
 *
 * This module wraps window.localStorage with encryption using crypto-js.
 * All sensitive data is encrypted with AES before being stored.
 *
 * IMPORTANT: Replace the default secret with a secure value in production.
 * You can do this by setting the REACT_APP_STORAGE_SECRET environment variable.
 *
 * Dependencies:
 *   npm install crypto-js
 *
 * Usage:
 *   import storage from './storage';
 *   storage.setToken('your-token');
 *   const token = storage.getToken();
 */

const CryptoJS = require('crypto-js');

const storagePrefix = 'msw-db';
// Use an environment variable if available, otherwise fallback to a default secret.
// NOTE: In production, ensure that the secret is kept secure.
const storageSecret = process.env.REACT_APP_STORAGE_SECRET || 'default-secret';

// Helper functions for encryption and decryption
const secureSetItem = (key, value) => {
  try {
    const encryptedData = CryptoJS.AES.encrypt(
      JSON.stringify(value),
      storageSecret
    ).toString();
    window.localStorage.setItem(key, encryptedData);
  } catch (error) {
    console.error(`Error setting item ${key}:`, error);
  }
};

const secureGetItem = (key) => {
  try {
    const encryptedData = window.localStorage.getItem(key);
    if (!encryptedData) return null;
    const bytes = CryptoJS.AES.decrypt(encryptedData, storageSecret);
    const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decryptedData);
  } catch (error) {
    console.error(`Error getting item ${key}:`, error);
    return null;
  }
};

const secureRemoveItem = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing item ${key}:`, error);
  }
};

const storage = {
  // Push subscription
  checkPushSubscription: () =>
    secureGetItem(`${storagePrefix}_push_subscription`),
  subscribePushNotifications: (subscriptionStatus) =>
    secureSetItem(`${storagePrefix}_push_subscription`, subscriptionStatus),

  // Token functions
  getToken: () => secureGetItem(`${storagePrefix}token`),
  setToken: (token) => secureSetItem(`${storagePrefix}token`, token),
  clearToken: () => secureRemoveItem(`${storagePrefix}token`),

  // Temporary data
  getTempData: () => secureGetItem(`${storagePrefix}__temp`),
  setTempData: (tempData) => secureSetItem(`${storagePrefix}__temp`, tempData),
  clearTempData: () => secureRemoveItem(`${storagePrefix}__temp`),

  // Analytics data
  getAnalyticsData: () => secureGetItem(`${storagePrefix}__analytics`),
  setAnalyticsData: (analyticsData) =>
    secureSetItem(`${storagePrefix}__analytics`, analyticsData),
  clearAnalyticsData: () => secureRemoveItem(`${storagePrefix}__analytics`),

  // Temporary token
  getTempToken: () => secureGetItem(`${storagePrefix}token__temp`),
  setTempToken: (token) => secureSetItem(`${storagePrefix}token__temp`, token),

  // Session key
  getSessionKey: () => secureGetItem(`${storagePrefix}session-key`),
  setSessionKey: (key) => secureSetItem(`${storagePrefix}session-key`, key),

  // User IP address (encrypted for privacy)
  getUserIpAddress: () => secureGetItem(`${storagePrefix}_user_ip`),
  setUserIpAddress: (ipAddress) =>
    secureSetItem(`${storagePrefix}_user_ip`, ipAddress),
  clearUserIpAddress: () => secureRemoveItem(`${storagePrefix}_user_ip`),

  // Generic secure data functions
  getSecureData: (key) => secureGetItem(`${storagePrefix}_${key}`),
  setSecureData: (key, data) => secureSetItem(`${storagePrefix}_${key}`, data),
  clearSecureData: (key) => secureRemoveItem(`${storagePrefix}_${key}`),
};

export default storage;
